// Email to Todolix addresses (the kit's MailAddresses.swift): one for the Inbox, and one for any area or project that
// wants one, kept in `mailAddresses/{local}` where the mail function looks them up. Readable in a list of recent
// recipients ("half-marathon-4fq8k2@in.todolix.app"), hard to guess thanks to the random end; it points at the
// record's id, so renaming or moving it never breaks the address. The Firestore rules let a person make, read and
// remove only their own; a taken address is refused, so a clash tries another ending. The Firestore side is in
// mailFirestore.ts, so the demo doesn't load Firebase.

export const MAIL_DOMAIN = "in.todolix.app";

export type MailKind = "inbox" | "area" | "project";

export interface MailAddress {
  /** The part before the @, and the document's id. */
  local: string;
  kind: MailKind;
  targetID?: string;
  /** The area's or project's name when the address was made. */
  label: string;
  active: boolean;
}

export function emailOf(address: MailAddress): string {
  return `${address.local}@${MAIL_DOMAIN}`;
}

/** The address that leads to the Inbox, an area or a project, if it has one. */
export function addressFor(addresses: readonly MailAddress[], kind: MailKind, targetID?: string): MailAddress | undefined {
  return addresses.find((address) => address.kind === kind && address.targetID === targetID && address.active);
}

/** Without letters that are easily mixed up (0/o, 1/l). */
const ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";

export function randomPart(count: number, random: () => number = () => crypto.getRandomValues(new Uint32Array(1))[0]! / 2 ** 32): string {
  let result = "";
  for (let index = 0; index < count; index += 1) result += ALPHABET[Math.floor(random() * ALPHABET.length)];
  return result;
}

/** Lower-case letters and digits joined by hyphens: "Tax return 2026 ✈️" → "tax-return-2026" (MailAddresses.slug). */
export function slug(text: string, limit = 24): string {
  const folded = text.normalize("NFD").replace(/\p{M}/gu, "").replace(/ß/g, "ss").toLowerCase();
  let result = "";
  for (const character of folded) {
    if (/[a-z0-9]/.test(character)) result += character;
    else if (result !== "" && !result.endsWith("-")) result += "-";
  }
  result = result.slice(0, limit);
  while (result.endsWith("-")) result = result.slice(0, -1);
  return result;
}

/** "inbox-x7k2m9qd" for the Inbox, "half-marathon-4fq8k2" for a project. */
export function mailLocal(kind: MailKind, label: string, random?: () => number): string {
  if (kind === "inbox") return `inbox-${randomPart(8, random)}`;
  const readable = slug(label);
  return `${readable === "" ? kind : readable}-${randomPart(6, random)}`;
}

export interface MailService {
  /** The person's addresses, now and whenever they change. */
  subscribe(listener: (addresses: MailAddress[]) => void): () => void;
  create(kind: MailKind, targetID: string | undefined, label: string): Promise<MailAddress>;
  /** A new address in place of the old one, which stops working at once. */
  renew(address: MailAddress, label: string): Promise<MailAddress>;
  remove(address: MailAddress): Promise<void>;
}

/** Addresses in memory, for the demo and the tests. */
export function memoryMail(initial: MailAddress[] = []): MailService {
  let addresses = [...initial];
  const listeners = new Set<(addresses: MailAddress[]) => void>();
  const changed = () => {
    addresses = [...addresses].sort((a, b) => a.local.localeCompare(b.local));
    for (const listener of listeners) listener(addresses);
  };
  const service: MailService = {
    subscribe(listener) {
      listeners.add(listener);
      listener(addresses);
      return () => listeners.delete(listener);
    },
    async create(kind, targetID, label) {
      const address: MailAddress = { local: mailLocal(kind, label), kind, label, active: true, ...(targetID ? { targetID } : {}) };
      addresses.push(address);
      changed();
      return address;
    },
    async renew(address, label) {
      const next = await service.create(address.kind, address.targetID, label);
      await service.remove(address);
      return next;
    },
    async remove(address) {
      addresses = addresses.filter((other) => other.local !== address.local);
      changed();
    },
  };
  return service;
}
